import React, { useState, useEffect, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Shield, Database, FolderArchive, RefreshCw, Download, AlertTriangle, CheckCircle, Clock, Users, Activity, Mail, Send } from "lucide-react";

export default function AdminSecurityPage() {
  const { user } = useAuth();
  const [healthCheck, setHealthCheck] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [emailConfig, setEmailConfig] = useState(null);
  const [editingSenderEmail, setEditingSenderEmail] = useState("");
  const [loading, setLoading] = useState({ health: false, audit: false, backup: false, dbDownload: false, filesDownload: false, bulkInvite: false, emailSave: false });
  const [activeTab, setActiveTab] = useState("health");

  const runHealthCheck = useCallback(async () => {
    setLoading(p => ({ ...p, health: true }));
    try {
      const res = await api.get("/admin/security/health-check");
      setHealthCheck(res.data);
    } catch (e) {
      toast.error("Failed to run health check");
    }
    setLoading(p => ({ ...p, health: false }));
  }, []);

  const loadAuditLogs = useCallback(async () => {
    setLoading(p => ({ ...p, audit: true }));
    try {
      const res = await api.get("/admin/security/audit-log?limit=100");
      setAuditLogs(res.data);
    } catch (e) {
      toast.error("Failed to load audit logs");
    }
    setLoading(p => ({ ...p, audit: false }));
  }, []);

  const loadBackupStatus = useCallback(async () => {
    setLoading(p => ({ ...p, backup: true }));
    try {
      const res = await api.get("/admin/backup/status");
      setBackupStatus(res.data);
    } catch (e) {
      toast.error("Failed to load backup status");
    }
    setLoading(p => ({ ...p, backup: false }));
  }, []);

  const loadEmailConfig = useCallback(async () => {
    try {
      const res = await api.get("/admin/email/config");
      setEmailConfig(res.data);
      setEditingSenderEmail(res.data.sender_email || "");
    } catch {}
  }, []);

  const saveEmailConfig = async () => {
    setLoading(p => ({ ...p, emailSave: true }));
    try {
      await api.put("/admin/email/config", { sender_email: editingSenderEmail });
      toast.success("Email sender updated!");
      loadEmailConfig();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
    setLoading(p => ({ ...p, emailSave: false }));
  };

  useEffect(() => {
    runHealthCheck();
    loadAuditLogs();
    loadBackupStatus();
    loadEmailConfig();
  }, [runHealthCheck, loadAuditLogs, loadBackupStatus, loadEmailConfig]);

  const downloadBackup = async (type) => {
    const key = type === "database" ? "dbDownload" : "filesDownload";
    setLoading(p => ({ ...p, [key]: true }));
    try {
      const res = await api.get(`/admin/backup/${type}`, { 
        responseType: "blob",
        timeout: 120000 // 2 min timeout for large backups
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `smartorange_${type}_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${type === "database" ? "Database" : "Files"} backup downloaded`);
    } catch (e) {
      console.error("Download error:", e);
      toast.error(e.response?.data?.detail || "Download failed. Try again.");
    }
    setLoading(p => ({ ...p, [key]: false }));
  };

  const triggerManualBackup = async () => {
    setLoading(p => ({ ...p, backup: true }));
    try {
      await api.post("/admin/backup/trigger");
      toast.success("Backup created successfully!");
      loadBackupStatus();
    } catch (e) {
      toast.error("Failed to create backup");
    }
    setLoading(p => ({ ...p, backup: false }));
  };

  const fixUserAccess = async (userId, userName) => {
    try {
      await api.post(`/admin/security/fix-user/${userId}`);
      toast.success(`Access restored for ${userName}`);
      runHealthCheck();
    } catch (e) {
      toast.error("Failed to fix access");
    }
  };

  const resendInvite = async (userId, userName) => {
    try {
      await api.post(`/admin/resend-invite/${userId}`);
      toast.success(`Invite sent to ${userName}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send invite");
    }
  };

  const bulkSendInvites = async () => {
    setLoading(p => ({ ...p, bulkInvite: true }));
    try {
      const res = await api.post("/admin/bulk-invite");
      toast.success(res.data.message);
    } catch (e) {
      toast.error("Failed to send bulk invites");
    }
    setLoading(p => ({ ...p, bulkInvite: false }));
  };

  const tabs = [
    { id: "health", label: "Health Check", icon: Activity },
    { id: "backup", label: "Backups", icon: Database },
    { id: "email", label: "Email", icon: Mail },
    { id: "audit", label: "Audit Log", icon: Shield },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="admin-security-page">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center">
          <Shield className="w-5 h-5 text-orange-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-zinc-50">Security & Backup</h1>
          <p className="text-sm text-zinc-500">Monitor access, download backups, review audit logs</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-zinc-900 rounded-xl p-1 w-fit">
        {tabs.map(tab => (
          <button
            key={tab.id}
            data-testid={`security-tab-${tab.id}`}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id ? "bg-zinc-800 text-orange-400" : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Health Check Tab */}
      {activeTab === "health" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-50">User Access Health Check</h2>
            <button
              data-testid="run-health-check-btn"
              onClick={runHealthCheck}
              disabled={loading.health}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-50 rounded-lg text-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading.health ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              data-testid="bulk-invite-btn"
              onClick={bulkSendInvites}
              disabled={loading.bulkInvite}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 rounded-lg text-sm font-medium transition-colors"
            >
              <Send className={`w-4 h-4 ${loading.bulkInvite ? "animate-pulse" : ""}`} />
              {loading.bulkInvite ? "Sending..." : "Invite All Without Password"}
            </button>
          </div>

          {healthCheck && (
            <>
              {/* Stats cards */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Users className="w-4 h-4 text-zinc-500" />
                    <span className="text-sm text-zinc-500">Total Users</span>
                  </div>
                  <span className="text-2xl font-bold text-zinc-50">{healthCheck.stats.total}</span>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <span className="text-sm text-zinc-500">Healthy</span>
                  </div>
                  <span className="text-2xl font-bold text-emerald-400">{healthCheck.stats.healthy}</span>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                    <span className="text-sm text-zinc-500">Issues</span>
                  </div>
                  <span className="text-2xl font-bold text-red-400">{healthCheck.stats.issues}</span>
                </div>
              </div>

              {/* Users with issues first, then healthy */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-zinc-800">
                      <th className="text-left p-3 text-zinc-500 font-medium">User</th>
                      <th className="text-left p-3 text-zinc-500 font-medium">Role</th>
                      <th className="text-left p-3 text-zinc-500 font-medium">Access</th>
                      <th className="text-left p-3 text-zinc-500 font-medium">Password</th>
                      <th className="text-left p-3 text-zinc-500 font-medium">Issues</th>
                      <th className="text-right p-3 text-zinc-500 font-medium">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...healthCheck.users]
                      .sort((a, b) => (a.status === "issue" ? -1 : 1) - (b.status === "issue" ? -1 : 1))
                      .map(u => (
                        <tr key={u.id} className={`border-b border-zinc-800/50 ${u.status === "issue" ? "bg-red-500/5" : ""}`}>
                          <td className="p-3">
                            <div className="text-zinc-50 font-medium">{u.name || "—"}</div>
                            <div className="text-xs text-zinc-500">{u.email || "no email"}</div>
                          </td>
                          <td className="p-3 text-zinc-400">{u.role}</td>
                          <td className="p-3">
                            {u.has_portal_access ? (
                              <span className="text-emerald-400 text-xs">Active</span>
                            ) : (
                              <span className="text-red-400 text-xs font-medium">No Access</span>
                            )}
                          </td>
                          <td className="p-3">
                            {u.has_password ? (
                              <span className="text-emerald-400 text-xs">Set</span>
                            ) : (
                              <span className="text-amber-400 text-xs">Missing</span>
                            )}
                          </td>
                          <td className="p-3">
                            {u.issues.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {u.issues.map(i => (
                                  <span key={i} className="px-2 py-0.5 bg-red-500/10 text-red-400 rounded text-xs">{i}</span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-emerald-400 text-xs">OK</span>
                            )}
                          </td>
                          <td className="p-3 text-right">
                            <div className="flex gap-1 justify-end">
                              {u.issues.includes("no_portal_access") && (
                                <button
                                  data-testid={`fix-user-${u.id}`}
                                  onClick={() => fixUserAccess(u.id, u.name)}
                                  className="px-3 py-1 bg-orange-500/10 text-orange-400 hover:bg-orange-500/20 rounded text-xs font-medium transition-colors"
                                >
                                  Fix Access
                                </button>
                              )}
                              {u.issues.includes("no_password") && u.email && (
                                <button
                                  data-testid={`invite-user-${u.id}`}
                                  onClick={() => resendInvite(u.id, u.name)}
                                  className="px-3 py-1 bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 rounded text-xs font-medium transition-colors"
                                >
                                  Send Invite
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Backup Tab */}
      {activeTab === "backup" && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-zinc-50">Backup Management</h2>

          {backupStatus && (
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Database className="w-4 h-4 text-blue-400" />
                  <span className="text-sm text-zinc-400">Database</span>
                </div>
                <div className="text-zinc-50 font-semibold">{backupStatus.database.collections} collections</div>
                <div className="text-xs text-zinc-500">{backupStatus.database.total_documents?.toLocaleString()} documents</div>
              </div>
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <FolderArchive className="w-4 h-4 text-purple-400" />
                  <span className="text-sm text-zinc-400">Files</span>
                </div>
                <div className="text-zinc-50 font-semibold">{backupStatus.files.total_files} files</div>
                <div className="text-xs text-zinc-500">Uploaded images & documents</div>
              </div>
            </div>
          )}

          {/* Auto backup status */}
          {backupStatus?.auto_backup && (
            <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-emerald-400" />
                </div>
                <div>
                  <div className="text-zinc-50 font-medium">Auto Backup: Daily</div>
                  <div className="text-xs text-zinc-500">
                    Last: {backupStatus.auto_backup.last_backup
                      ? new Date(backupStatus.auto_backup.last_backup).toLocaleString()
                      : "Not yet run"}
                    {backupStatus.auto_backup.last_backup_collections && 
                      ` (${backupStatus.auto_backup.last_backup_collections} collections)`}
                  </div>
                </div>
              </div>
              <button
                onClick={triggerManualBackup}
                disabled={loading.backup}
                data-testid="trigger-backup-btn"
                className="flex items-center gap-2 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs transition-colors disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loading.backup ? "animate-spin" : ""}`} />
                {loading.backup ? "Creating..." : "Run Now"}
              </button>
            </div>
          )}

          {/* Download buttons */}
          <div className="grid grid-cols-2 gap-4">
            <button
              data-testid="download-db-backup-btn"
              onClick={() => downloadBackup("database")}
              disabled={loading.dbDownload}
              className="flex items-center justify-center gap-3 p-6 bg-zinc-900 border border-zinc-800 hover:border-orange-500/30 rounded-xl transition-colors group"
            >
              <Download className={`w-5 h-5 text-orange-400 ${loading.dbDownload ? "animate-bounce" : ""}`} />
              <div className="text-left">
                <div className="text-zinc-50 font-medium group-hover:text-orange-400 transition-colors">
                  {loading.dbDownload ? "Preparing..." : "Download Database"}
                </div>
                <div className="text-xs text-zinc-500">Full MongoDB dump as ZIP</div>
              </div>
            </button>
            <button
              data-testid="download-files-backup-btn"
              onClick={() => downloadBackup("files")}
              disabled={loading.filesDownload}
              className="flex items-center justify-center gap-3 p-6 bg-zinc-900 border border-zinc-800 hover:border-orange-500/30 rounded-xl transition-colors group"
            >
              <Download className={`w-5 h-5 text-orange-400 ${loading.filesDownload ? "animate-bounce" : ""}`} />
              <div className="text-left">
                <div className="text-zinc-50 font-medium group-hover:text-orange-400 transition-colors">
                  {loading.filesDownload ? "Preparing..." : "Download Files"}
                </div>
                <div className="text-xs text-zinc-500">All uploaded images & docs</div>
              </div>
            </button>
          </div>
        </div>
      )}


      {/* Email Config Tab */}
      {activeTab === "email" && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-zinc-50">Email Configuration</h2>
          
          {!emailConfig ? (
            <button onClick={loadEmailConfig} className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-50 rounded-lg text-sm">
              Load Config
            </button>
          ) : (
            <div className="space-y-4">
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-4">
                <div className="flex items-center gap-3">
                  <div className={`w-3 h-3 rounded-full ${emailConfig.api_key_set ? "bg-green-500" : "bg-red-500"}`} />
                  <span className="text-sm text-zinc-400">Resend API Key: {emailConfig.api_key_set ? "Configured" : "Not Set"}</span>
                </div>
                
                <div>
                  <label className="text-xs text-zinc-500 block mb-1">Sender Email (From address)</label>
                  <div className="flex gap-2">
                    <input
                      type="email"
                      value={editingSenderEmail}
                      onChange={(e) => setEditingSenderEmail(e.target.value)}
                      className="flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-50 text-sm"
                      placeholder="noreply@yourdomain.com"
                      data-testid="sender-email-input"
                    />
                    <button
                      onClick={saveEmailConfig}
                      disabled={loading.emailSave || editingSenderEmail === emailConfig.sender_email}
                      data-testid="save-email-config-btn"
                      className="px-4 py-2 bg-orange-500 hover:bg-orange-600 disabled:bg-zinc-700 disabled:text-zinc-500 text-white rounded-lg text-sm font-medium transition-colors"
                    >
                      {loading.emailSave ? "Saving..." : "Save"}
                    </button>
                  </div>
                  <p className="text-[10px] text-zinc-600 mt-1">
                    Domain must be verified in Resend (resend.com/domains). Current: {emailConfig.sender_email}
                  </p>
                </div>
              </div>
              
              <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
                <h3 className="text-sm font-medium text-zinc-300 mb-2">Test Email</h3>
                <button
                  onClick={async () => {
                    try {
                      await api.post("/admin/test-email");
                      toast.success("Test email sent to admin");
                    } catch (e) {
                      toast.error(e.response?.data?.detail || "Failed to send test email");
                    }
                  }}
                  data-testid="send-test-email-btn"
                  className="px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-50 rounded-lg text-sm transition-colors"
                >
                  <Mail className="w-4 h-4 inline mr-2" />
                  Send Test Email
                </button>
              </div>
            </div>
          )}
        </div>
      )}


      {/* Audit Log Tab */}
      {activeTab === "audit" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-zinc-50">Staff Audit Log</h2>
            <button
              onClick={loadAuditLogs}
              disabled={loading.audit}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-zinc-50 rounded-lg text-sm transition-colors"
            >
              <RefreshCw className={`w-4 h-4 ${loading.audit ? "animate-spin" : ""}`} />
              Refresh
            </button>
          </div>

          <div className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
            {auditLogs.length === 0 ? (
              <div className="p-8 text-center text-zinc-500">No audit logs found</div>
            ) : (
              <div className="divide-y divide-zinc-800/50 max-h-[600px] overflow-y-auto">
                {auditLogs.map((log, i) => (
                  <div key={i} className="p-4 hover:bg-zinc-800/30">
                    <div className="flex items-start justify-between">
                      <div>
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium mr-2 ${
                          log.action?.includes("EMERGENCY") ? "bg-red-500/10 text-red-400" :
                          log.action?.includes("FIX") ? "bg-amber-500/10 text-amber-400" :
                          log.action?.includes("DELETE") ? "bg-red-500/10 text-red-400" :
                          "bg-zinc-700 text-zinc-300"
                        }`}>
                          {log.action || "CHANGE"}
                        </span>
                        <span className="text-zinc-300 text-sm">
                          {log.target_user_name || log.target_email || log.target_user_id || "—"}
                        </span>
                      </div>
                      <span className="text-xs text-zinc-600">
                        {log.timestamp ? new Date(log.timestamp).toLocaleString() : "—"}
                      </span>
                    </div>
                    {log.changed_by_name && (
                      <div className="text-xs text-zinc-500 mt-1">by {log.changed_by_name}</div>
                    )}
                    {log.reason && (
                      <div className="text-xs text-zinc-500 mt-1">Reason: {log.reason}</div>
                    )}
                    {log.changes && (
                      <div className="text-xs text-zinc-600 mt-1 font-mono">
                        {JSON.stringify(log.changes).slice(0, 200)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
